import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Router, ActivatedRoute, UrlSegment } from '@angular/router';

const SUPPORTED_LANGS = ['en', 'es', 'pt', 'zh-CN'];

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private currentLang$ = new BehaviorSubject<string>('en');

  constructor(private router: Router, private route: ActivatedRoute) {
    const initial = this.detectLangFromUrl();
    this.currentLang$.next(initial);
  }

  get lang$() {
    return this.currentLang$.asObservable();
  }

  get lang(): string {
    return this.currentLang$.value;
  }

  setLanguage(lang: string) {
    const normalized = this.normalizeLang(lang);
    const targetUrl = this.replaceLangInCurrentUrl(normalized);
    this.currentLang$.next(normalized);
    this.router.navigateByUrl(targetUrl, { replaceUrl: true });
  }

  build(pathWithoutLang: string, lang?: string): any[] {
    const l = this.normalizeLang(lang || this.lang);
    const clean = (pathWithoutLang || '').replace(/^\/+|\/+$/g, '');
    const segments = clean ? clean.split('/').filter(Boolean) : [];
    return ['/', ...segments, l];
  }

  buildProductRoute(productId: string | number, productTitle: string, lang?: string): any[] {
    const l = this.normalizeLang(lang || this.lang);
    const idSegment = String(productId).trim();
    const slug = this.slugify(String(productTitle || '').trim()) || 'tour';
    return ['/', idSegment, slug, l];
  }

  private normalizeLang(lang: string): string {
    const lower = (lang || '').toLowerCase();
    if (SUPPORTED_LANGS.map(l => l.toLowerCase()).includes(lower)) return lower;
    return 'en';
  }

  private slugify(input: string): string {
    return (input || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  private detectLangFromUrl(): string {
    try {
      const url = this.router.url || '/home/en';
      const segs = url.split('/').filter(Boolean);
      // language is last segment in our scheme
      const last = segs[segs.length - 1] || 'en';
      return this.normalizeLang(last);
    } catch {
      return 'en';
    }
  }

  private replaceLangInCurrentUrl(targetLang: string): string {
    try {
      const url = this.router.url || '/home/en';
      const qIndex = url.indexOf('?');
      const hashIndex = url.indexOf('#');
      const endIndex = [qIndex, hashIndex].filter(i => i >= 0).sort()[0] ?? url.length;
      const path = url.substring(0, endIndex);
      const rest = url.substring(endIndex);
      const parts = path.split('/').filter(Boolean);
      if (parts.length === 0) return `/home/${targetLang}${rest}`;
      // Replace last segment with lang
      parts[parts.length - 1] = targetLang;
      return '/' + parts.join('/') + rest;
    } catch {
      return `/home/${targetLang}`;
    }
  }
}
